import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { 
  Calendar, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  Euro, 
  Home,
  ArrowRight,
  Wrench
} from 'lucide-react';
import { maintenanceService } from '@/services/maintenanceService';

/**
 * MaintenanceSummaryWidget - Compact Dashboard Version
 * 
 * Features:
 * - Uses global owner and property filters from Dashboard
 * - No own dropdowns (uses global filters)
 * - Compact display with key metrics only
 * - Click navigation to full Maintenance page
 * - Consistent spacing with other dashboard widgets
 */
const MaintenanceSummaryWidget = ({ selectedOwner, selectedProperty }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [maintenanceData, setMaintenanceData] = useState({
    upcoming: [],
    overdue: [],
    totalCost: 0,
    affectedProperties: 0
  });

  useEffect(() => {
    loadMaintenanceData();
  }, [selectedOwner, selectedProperty]);

  const loadMaintenanceData = async () => {
    try {
      setLoading(true);
      
      let maintenanceItems = [];
      
      // Load maintenance data based on filters
      if (selectedProperty && selectedProperty !== 'all') {
        // Load for specific property
        maintenanceItems = await maintenanceService.getMaintenanceTasksForProperty(selectedProperty);
      } else if (selectedOwner && selectedOwner !== 'all') {
        // Load for specific owner
        maintenanceItems = await maintenanceService.getMaintenanceTasksForOwner(selectedOwner);
      } else {
        // Load all maintenance
        maintenanceItems = await maintenanceService.getAllMaintenanceTasks();
      }

      // Process data
      const now = new Date();
      const upcoming = maintenanceItems.filter(item => {
        const scheduledDate = new Date(item.scheduled_date);
        return scheduledDate >= now && item.status !== 'abgeschlossen';
      });

      const overdue = maintenanceItems.filter(item => {
        const scheduledDate = new Date(item.scheduled_date);
        return scheduledDate < now && item.status !== 'abgeschlossen';
      });

      // Calculate total estimated cost for upcoming and overdue
      const totalCost = [...upcoming, ...overdue].reduce((sum, item) => {
        return sum + (parseFloat(item.estimated_cost) || 0);
      }, 0);

      // Count affected properties
      const affectedPropertyIds = new Set([...upcoming, ...overdue].map(item => item.property_id));

      setMaintenanceData({
        upcoming,
        overdue,
        totalCost,
        affectedProperties: affectedPropertyIds.size
      });

    } catch (error) {
      console.error('❌ Error loading maintenance data:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('de-DE', {
      style: 'currency',
      currency: 'EUR'
    }).format(amount);
  };

  const formatRelativeDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffInMs = now - date;
    const diffInDays = Math.floor(diffInMs / (1000 * 60 * 60 * 24));
    
    if (diffInDays === 0) return 'Heute';
    if (diffInDays === 1) return 'Morgen';
    if (diffInDays === -1) return 'Gestern';
    if (diffInDays > 0) return `Vor ${diffInDays} Tagen`;
    return `In ${Math.abs(diffInDays)} Tagen`;
  };

  const handleNavigateToMaintenance = () => {
    navigate('/maintenance');
  };

  const handleMaintenanceClick = (maintenanceId) => {
    navigate(`/maintenance?highlight=${maintenanceId}`);
  };

  if (loading) {
    return (
      <Card className="mb-8">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Wartungsübersicht
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="animate-pulse space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-20 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
              ))}
            </div>
            <div className="h-32 bg-gray-200 dark:bg-gray-700 rounded-lg"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="mb-8">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Wrench className="h-5 w-5" />
            Wartungsübersicht
          </CardTitle>
          <Button 
            variant="outline" 
            size="sm"
            onClick={handleNavigateToMaintenance}
            className="flex items-center gap-1"
          >
            Alle anzeigen
            <ArrowRight className="h-4 w-4" />
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* KPI Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          {/* Upcoming */}
          <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg border border-blue-200 dark:border-blue-800">
            <div className="flex items-center gap-2 mb-2">
              <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
              <span className="text-sm font-medium text-blue-900 dark:text-blue-100">
                Anstehend
              </span>
            </div>
            <div className="text-2xl font-bold text-blue-900 dark:text-blue-100">
              {maintenanceData.upcoming.length}
            </div>
          </div>

          {/* Overdue */}
          <div className="bg-red-50 dark:bg-red-900/20 p-4 rounded-lg border border-red-200 dark:border-red-800">
            <div className="flex items-center gap-2 mb-2">
              <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
              <span className="text-sm font-medium text-red-900 dark:text-red-100">
                Überfällig
              </span>
            </div>
            <div className="text-2xl font-bold text-red-900 dark:text-red-100">
              {maintenanceData.overdue.length}
            </div>
          </div>

          {/* Total Cost */}
          <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg border border-green-200 dark:border-green-800">
            <div className="flex items-center gap-2 mb-2">
              <Euro className="h-4 w-4 text-green-600 dark:text-green-400" />
              <span className="text-sm font-medium text-green-900 dark:text-green-100">
                Geschätzte Kosten
              </span>
            </div>
            <div className="text-lg font-bold text-green-900 dark:text-green-100">
              {formatCurrency(maintenanceData.totalCost)}
            </div>
          </div>

          {/* Affected Properties */}
          <div className="bg-purple-50 dark:bg-purple-900/20 p-4 rounded-lg border border-purple-200 dark:border-purple-800">
            <div className="flex items-center gap-2 mb-2">
              <Home className="h-4 w-4 text-purple-600 dark:text-purple-400" />
              <span className="text-sm font-medium text-purple-900 dark:text-purple-100">
                Betroffene Objekte
              </span>
            </div>
            <div className="text-2xl font-bold text-purple-900 dark:text-purple-100">
              {maintenanceData.affectedProperties}
            </div>
          </div>
        </div>

        {/* Recent Maintenance Items */}
        <div>
          <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
            Nächste Wartungen
          </h4>
          
          {maintenanceData.upcoming.length === 0 && maintenanceData.overdue.length === 0 ? (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              <Calendar className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p className="font-medium">Keine anstehenden Wartungen</p>
              <p className="text-sm">Alle Wartungen sind auf dem neuesten Stand!</p>
            </div>
          ) : (
            <div className="space-y-2">
              {/* Show overdue first */}
              {maintenanceData.overdue.slice(0, 2).map((maintenance) => (
                <div
                  key={maintenance.id}
                  onClick={() => handleMaintenanceClick(maintenance.id)}
                  className="flex items-center justify-between p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg cursor-pointer hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <AlertTriangle className="h-4 w-4 text-red-600 dark:text-red-400" />
                    <div>
                      <div className="font-medium text-red-900 dark:text-red-100">
                        {maintenance.title}
                      </div>
                      <div className="text-sm text-red-700 dark:text-red-300">
                        {maintenance.address} • {formatRelativeDate(maintenance.scheduled_date)}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-red-900 dark:text-red-100">
                    {formatCurrency(maintenance.estimated_cost)}
                  </div>
                </div>
              ))}

              {/* Show upcoming */}
              {maintenanceData.upcoming.slice(0, 3).map((maintenance) => (
                <div
                  key={maintenance.id}
                  onClick={() => handleMaintenanceClick(maintenance.id)}
                  className="flex items-center justify-between p-3 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Clock className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    <div>
                      <div className="font-medium text-gray-900 dark:text-white">
                        {maintenance.title}
                      </div>
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        {maintenance.address} • {formatRelativeDate(maintenance.scheduled_date)}
                      </div>
                    </div>
                  </div>
                  <div className="text-sm font-medium text-gray-900 dark:text-white">
                    {formatCurrency(maintenance.estimated_cost)}
                  </div>
                </div>
              ))}

              {/* Show more indicator */}
              {(maintenanceData.upcoming.length + maintenanceData.overdue.length) > 5 && (
                <div 
                  onClick={handleNavigateToMaintenance}
                  className="text-center py-2 text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 cursor-pointer"
                >
                  +{(maintenanceData.upcoming.length + maintenanceData.overdue.length) - 5} weitere Wartungen anzeigen
                </div>
              )}
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
};

export default MaintenanceSummaryWidget;

